import {
  IconHandshakeFilled,
  IconMessageQuestionFilled,
  IconProfileFilled,
  IconTourDoorFilled,
} from '@zillow/constellation-icons';
import { SatelliteConversationMessageType } from '@zg-rentals/ts-satellite-client';
import { MESSAGE_TYPES } from './messageTypes';

/**
 * Base icon mapping for high-priority system messages (highPriorityWithLogo).
 *
 * Dual-keyed during the GraphQL → Proxit migration: the legacy camelCase keys serve
 * consumers still on GraphQL-shaped data (SRW), the `SatelliteConversationMessageType`
 * keys serve consumers reading raw Proxit DTOs (SLW). Collapse to enum keys once SRW
 * migrates. `tourRescheduledConfirmed` is camelCase-only on purpose: it has no
 * `SatelliteConversationMessageType` member, so SLW (Proxit) never emits it — the key
 * is retained solely for SRW's camelCase path.
 */
const BASE_SYSTEM_MESSAGE_ICON_MAP = {
  // Applications
  [MESSAGE_TYPES.SYSTEM_MESSAGE.APPLICATIONS.APPLICATION_RECEIVED]: IconProfileFilled,
  [SatelliteConversationMessageType.APPLICATION_RECEIVED]: IconProfileFilled,

  // Leases
  [MESSAGE_TYPES.SYSTEM_MESSAGE.LEASES.LEASE_COUNTERSIGNING_COMPLETE]: IconHandshakeFilled,
  [SatelliteConversationMessageType.LEASE_COUNTERSIGNING_COMPLETE]: IconHandshakeFilled,

  // Tours
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_CONFIRMED]: IconTourDoorFilled,
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_INSTANT_BOOKED]: IconTourDoorFilled,
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_REQUEST_QUICK]: IconTourDoorFilled,
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_RESCHEDULED_CONFIRMED]: IconTourDoorFilled,
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_RESCHEDULED_INSTANT]: IconTourDoorFilled,
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_RESCHEDULED_REVIEW]: IconTourDoorFilled,
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_RESCHEDULED_BY_LANDLORD]: IconTourDoorFilled,
  [SatelliteConversationMessageType.TOUR_CONFIRMED]: IconTourDoorFilled,
  [SatelliteConversationMessageType.TOUR_INSTANT_BOOKED]: IconTourDoorFilled,
  [SatelliteConversationMessageType.TOUR_REQUEST_QUICK]: IconTourDoorFilled,
  [SatelliteConversationMessageType.TOUR_RESCHEDULED_INSTANT]: IconTourDoorFilled,
  [SatelliteConversationMessageType.TOUR_RESCHEDULED_REVIEW]: IconTourDoorFilled,
  [SatelliteConversationMessageType.TOUR_RESCHEDULED_BY_LANDLORD]: IconTourDoorFilled,
} as const;

/**
 * Renter-specific icon overrides
 */
const RENTER_ICON_OVERRIDES = {
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_REQUEST_QUICK]: IconMessageQuestionFilled,
  [MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS.TOUR_RESCHEDULED_REVIEW]: IconMessageQuestionFilled,
  [SatelliteConversationMessageType.TOUR_REQUEST_QUICK]: IconMessageQuestionFilled,
  [SatelliteConversationMessageType.TOUR_RESCHEDULED_REVIEW]: IconMessageQuestionFilled,
} as const;

/**
 * Icon mapping for landlord-facing high-priority system messages
 */
export const LANDLORD_SYSTEM_MESSAGE_ICON_MAP = BASE_SYSTEM_MESSAGE_ICON_MAP;

/**
 * Icon mapping for renter-facing high-priority system messages
 */
export const RENTER_SYSTEM_MESSAGE_ICON_MAP = {
  ...BASE_SYSTEM_MESSAGE_ICON_MAP,
  ...RENTER_ICON_OVERRIDES,
} as const;
